import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/common/Logo'
import { LanguageSwitch } from '@/components/common/LanguageSwitch'
import { usePinnedLightTheme } from '@/hooks/usePinnedLightTheme'

interface AuthSplitLayoutProps {
  children: ReactNode
}

/**
 * Two-pane shell shared by sign-in and the waitlist onboarding states
 * (design-of-record ui_kits/login/index.html and waitlist.html).
 * Left: warm-charcoal brand pane with wordmark, headline, subline, product still, amber
 * glow and scrim. Right: language codes, a centered 372px column with the narrow-screen
 * narrative header, and the copyright footer. The light scope is pinned while mounted.
 */
export function AuthSplitLayout({ children }: AuthSplitLayoutProps) {
  const { t } = useTranslation()
  usePinnedLightTheme()

  return (
    <div className="lp-root" data-testid="auth-split-layout">
      <a href="#auth-main" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50">
        {t('layout.skipToContent')}
      </a>
      <aside className="lp-brand" data-testid="auth-brand-pane">
        <div className="lp-glow" aria-hidden="true" />
        <img className="lp-still" src="/images/login-missions-still.png" alt="" aria-hidden="true" />
        <div className="lp-scrim" aria-hidden="true" />
        <a href="https://agion.dev" className="lp-wordmark lp-rise lp-rise-1">
          <Logo theme="dark" />
        </a>
        <div className="lp-copy">
          <h1 className="lp-headline lp-rise lp-rise-2">{t('login.headline')}</h1>
          <p className="lp-subline lp-rise lp-rise-3">{t('login.subline')}</p>
        </div>
      </aside>

      <main id="auth-main" className="lp-form-pane">
        <LanguageSwitch />
        <div className="lp-form-center">
          <div className="lp-form-inner">
            <div className="lp-narrow-header lp-rise lp-rise-1">
              <Logo theme="light" />
              <h1 className="lp-narrow-headline">{t('login.headline')}</h1>
              <p className="lp-narrow-subline">{t('login.sublineShort')}</p>
              <hr className="lp-hairline" />
            </div>
            {children}
          </div>
        </div>
        <footer className="lp-footer">
          {t('login.copyright', { year: new Date().getFullYear() })}
        </footer>
      </main>
    </div>
  )
}
